import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  HStack,
  VStack,
  Card,
  CardHeader,
  CardBody,
  useToast,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { FiDownload, FiRefreshCw, FiSettings } from 'react-icons/fi';
import { canvasAPI } from '../../services/api';
import { useLTI } from '../../contexts/LTIContext';

const TeachingAssistantView = () => {
  const navigate = useNavigate();
  const { isAdmin } = useLTI();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await canvasAPI.getCourseCertificates();
      setCertificates(response.certificates || []);
    } catch (error) {
      console.error('Error loading certificates:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los certificados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (certificateId) => {
    try {
      await canvasAPI.downloadCertificate(certificateId);
      toast({
        title: 'Descarga iniciada',
        description: 'El certificado se está descargando',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast({
        title: 'Error',
        description: 'No se pudo descargar el certificado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'issued':
        return 'green';
      case 'draft':
        return 'yellow';
      case 'revoked':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'issued':
        return 'Emitido';
      case 'draft':
        return 'Borrador';
      case 'revoked':
        return 'Revocado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">Cargando certificados...</Text>
      </Box>
    );
  }

  return (
    <Box maxW="7xl" mx="auto" py={8} px={4}>
      {/* Header */}
      <Box mb={6}>
        <HStack justify="space-between" align="start">
          <Box>
            <Heading size="xl" mb={2}>
              Vista de Asistente
            </Heading>
            <Text color="gray.600">
              Certificados emitidos en este curso
            </Text>
          </Box>
          <Button
            leftIcon={<FiRefreshCw />}
            variant="outline"
            onClick={loadCertificates}
          >
            Actualizar
          </Button>
        </HStack>
      </Box>

      {/* Certificates Table */}
      <Card>
        <CardHeader>
          <HStack justify="space-between">
            <Heading size="md">Certificados Emitidos</Heading>
            <Button
              leftIcon={<FiRefreshCw />}
              size="sm"
              onClick={loadCertificates}
              variant="ghost"
            >
              Actualizar
            </Button>
          </HStack>
        </CardHeader>
        <CardBody>
          {certificates.length === 0 ? (
            <VStack py={8} spacing={4}>
              <Text color="gray.600">No hay certificados emitidos aún</Text>
            </VStack>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Estudiante</Th>
                    <Th>Curso</Th>
                    <Th>Fecha Emisión</Th>
                    <Th>Calificación</Th>
                    <Th>Estado</Th>
                    <Th>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {certificates.map((cert) => (
                    <Tr key={cert.id}>
                      <Td>
                        <Text fontWeight="medium">{cert.student_name}</Text>
                      </Td>
                      <Td>{cert.course_name}</Td>
                      <Td>
                        {new Date(cert.issue_date).toLocaleDateString('es-ES')}
                      </Td>
                      <Td>
                        {cert.final_grade ? cert.final_grade.toFixed(1) : 'N/A'}
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(cert.status)}>
                          {getStatusText(cert.status)}
                        </Badge>
                      </Td>
                      <Td>
                        <Tooltip label="Descargar certificado">
                          <IconButton
                            icon={<FiDownload />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleDownload(cert.certificate_id)}
                            aria-label="Descargar certificado"
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default TeachingAssistantView;
